import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@twilio-paste/core/box';
import { Button } from '@twilio-paste/core/button';
import { Input } from '@twilio-paste/core/input';
import { Label } from '@twilio-paste/core/label';
import { Text } from '@twilio-paste/core/text';
import { isAxiosError } from 'axios';
import { pickBy } from 'lodash';

import { useSearchRelationshipsOimContact } from '../../modules/RelationshipsApiProxy';

import { SearchCustomerResult } from './SearchCustomerResult';

const queryKeys = ['email', 'phonePrimary', 'phoneSecondary'];

export function SearchRelationshipsApiOimCustomersPanel() {
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [params, setParams] = useState<Record<string, string>>({});
  const [localError, setLocalError] = useState<string | null>(null);

  const hasAtLeastOne = useMemo(() => queryKeys.some((key) => inputs[key] && inputs[key].trim() !== ''), [inputs]);

  const {
    data,
    error,
    isFetching,
    refetch,
    isFetched,
  } = useSearchRelationshipsOimContact(params);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInputs({ ...inputs, [event.target.name]: event.target.value });
    setLocalError(null);
  }, [inputs]);

  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);
    if (!hasAtLeastOne) {
      setLocalError('At least one filter must be provided.');
      return;
    }
    setParams(pickBy(inputs, Boolean));
  }, [hasAtLeastOne, inputs]);

  useEffect(() => {
    if (Object.keys(params).length > 0) {
      refetch();
    }
  }, [params, refetch]);

  const responseBody = useMemo(() =>
    isFetched && data
      ? JSON.stringify(data.data, null, 2)
      : isAxiosError(error)
        ? JSON.stringify(error.response?.data, null, 2)
        : ''
    , [isFetched, data, error]);

  const httpStatus = useMemo(() =>
    data
      ? data.status
      : error && isAxiosError(error) && error.response
        ? error.response.status
        : null
    , [data, error]);

  return (
    <Box
      borderStyle="solid"
      borderWidth="borderWidth10"
      borderColor="colorBorderWeak"
      borderRadius="borderRadius20"
      padding="space60"
      backgroundColor="colorBackgroundBody"
      minWidth="320px"
      minHeight="700px"
      width="100%"
      flex="1"
    >
      <Text as="h3" fontWeight="fontWeightBold" fontSize="fontSize30" marginBottom="space40" display="block">Search Relationships API | OIM Customer Endpoint</Text>
      <Box as="form" onSubmit={handleSubmit} marginBottom="space40">
        <Box marginBottom="space40">
          <Label htmlFor="email">Email</Label>
          <Input required={false} id="email" name="email" type="text" value={inputs['email'] || ''} onChange={handleChange} />
        </Box>
        <Box display="flex" columnGap="space40" marginBottom="space40">
          <Box flex="1">
            <Label htmlFor="phonePrimary">Phone Primary</Label>
            <Input required={false} id="phonePrimary" name="phonePrimary" type="text" value={inputs['phonePrimary'] || ''} onChange={handleChange} />
          </Box>
          <Box flex="1">
            <Label htmlFor="phoneSecondary">Phone Secondary</Label>
            <Input required={false} id="phoneSecondary" name="phoneSecondary" type="text" value={inputs['phoneSecondary'] || ''} onChange={handleChange} />
          </Box>
        </Box>
        <Button variant="primary" type="submit" loading={isFetching} disabled={isFetching}>
          Run Request
        </Button>
      </Box>
      {(localError) && (
        <Text as="div" color="colorTextError" marginBottom="space40">
          {localError}
        </Text>
      )}
      <SearchCustomerResult
        responseBody={responseBody}
        isFetched={isFetched}
        data={data}
        httpStatus={httpStatus}
      />
    </Box>
  );
};
